import React, { useEffect, useRef, useState } from 'react'
import { useConversation } from '@/hooks/useConversation'
import { useTypingIndicator } from '@/hooks/useTypingIndicator'
import { usePresence } from '@/hooks/usePresence'
import { formatDistanceToNow } from 'date-fns'
import { Send, Loader2, Circle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

interface ChatWindowProps {
  conversationId: string
  otherUser: {
    id: string
    display_name: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

export function ChatWindow({ conversationId, otherUser }: ChatWindowProps) {
  const { user } = useAuth()
  const { messages, loading, sendMessage, markAsRead, room } = useConversation(conversationId)
  const { isTyping, typingUsers, startTyping, stopTyping } = useTypingIndicator(room)
  const { isOtherUserOnline } = usePresence(room)
  const [inputValue, setInputValue] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout>()

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mark as read when conversation opens
  useEffect(() => {
    markAsRead()
  }, [conversationId, markAsRead])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)

    // Trigger typing indicator
    startTyping()

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping()
    }, 3000)
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!inputValue.trim() || sending) return

    try {
      setSending(true)
      await sendMessage(inputValue.trim())
      setInputValue('')
      stopTyping()
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  const displayName = otherUser.display_name || otherUser.full_name || 'Anonymous'

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative">
          {otherUser.avatar_url ? (
            <img
              src={otherUser.avatar_url}
              alt={displayName}
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-lg font-medium text-gray-600">
                {displayName[0]}
              </span>
            </div>
          )}
          {isOtherUserOnline && (
            <Circle
              className="absolute bottom-0 right-0 w-3 h-3 text-green-500 fill-current"
            />
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">{displayName}</h3>
          <p className="text-sm text-gray-500">
            {isOtherUserOnline ? 'Online' : 'Offline'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => {
          const isOwn = message.sender_id === user?.id
          
          return (
            <div
              key={message.id}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  isOwn
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-900'
                }`}
              >
                <p className="break-words">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    isOwn ? 'text-blue-100' : 'text-gray-500'
                  }`}
                >
                  {formatDistanceToNow(new Date(message.created_at), {
                    addSuffix: true
                  })}
                </p>
              </div>
            </div>
          )
        })}

        {/* Typing indicator */}
        {isTyping && typingUsers.includes(otherUser.id) && (
          <div className="flex justify-start">
            <div className="bg-gray-200 text-gray-600 px-4 py-2 rounded-lg">
              <span className="text-sm italic">{displayName} is typing...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend(e)
              }
            }}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || sending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
